import React from 'react';
import { Alert, Button, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { PagedListState } from '@/hooks/usePagedList';

const { Text } = Typography;

/**
 * A tab title with its list's count: a spinner while the first page loads, a warning when it failed, else the
 * count of every flagged row (none for a ranking).
 */
export const ListTabLabel: React.FC<{
  title: string;
  state: PagedListState<unknown, unknown>;
  color?: string;
}> = ({ title, state, color = 'error' }) => {
  const total = state.list?.total;
  return (
    <span>
      {title}
      {state.loading ? (
        <Spin size="small" style={{ marginLeft: 8 }} />
      ) : state.failed ? (
        <Tooltip title="Not loaded">
          <WarningOutlined style={{ marginLeft: 8, color: '#faad14' }} />
        </Tooltip>
      ) : total ? (
        <Tag style={{ marginLeft: 4 }} color={color}>
          {total.toLocaleString()}
        </Tag>
      ) : null}
    </span>
  );
};

/** First-page states of a list: loading, or failed with a retry. Renders `children` once loaded. */
export const ListBody: React.FC<{
  state: PagedListState<unknown, unknown>;
  children: React.ReactNode;
}> = ({ state, children }) => {
  if (state.loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }
  if (state.failed) {
    // A failed load must not read as "nothing flagged".
    return (
      <Alert
        type="warning"
        showIcon
        message="Couldn't load this list for this window."
        description="A cold list can take up to a minute on the server. Retry, or come back in a minute."
        action={<Button onClick={state.reload}>Retry</Button>}
      />
    );
  }
  return <>{children}</>;
};

/** Under a list's table: how many rows are shown of how many, and the Load more button. */
export const LoadMoreFooter: React.FC<{
  state: PagedListState<unknown, unknown>;
  noun: string;
}> = ({ state, noun }) => {
  const list = state.list;
  if (!list || list.rows.length === 0) return null;
  const of = list.total ?? (state.hasMore ? undefined : list.rows.length);
  return (
    <Space wrap style={{ marginTop: 12 }}>
      <Text type="secondary">
        Showing {list.rows.length.toLocaleString()}
        {of !== undefined ? ` of ${of.toLocaleString()}` : ''} {noun}
      </Text>
      {state.hasMore && (
        <Button size="small" loading={state.loadingMore} onClick={state.loadMore}>
          Load more
        </Button>
      )}
      {state.moreFailed && <Text type="danger">Couldn&apos;t load more. Try again.</Text>}
    </Space>
  );
};
